import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { FILE_UPLOAD_PURPOSE } from '@shafa/shared';
import { removeAuthInfo } from '../../store/slices/authSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOut } from '@fortawesome/free-solid-svg-icons';
import { Toast } from '../../utils/SwalUti';
import { postDoctorImageUpload } from './postDoctorImageUpload';
import { detailsFileDisplayUrl, detailsFileSlotId, hasDetailsFileSlot } from './detailsFiles';
import './user-profile.scss'

const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/svg+xml';

const EducationalInfo = ({
    setInfoFun,
    info,
    handleSubmit,
    setDetailsFileField,
    isSubmitting,
}) => {
    const { selectedRole } = useSelector((state) => state.auth);
    const dispatch = useDispatch()
    const [signUrl, setSignUrl] = useState("")
    const [uploading, setUploading] = useState({})
    const [previewUrls, setPreviewUrls] = useState({})

    const onSubmit = (event) => {
        event.preventDefault()
        handleSubmit()
    }

    const handleLogout = () => {
        dispatch(removeAuthInfo())
        localStorage.clear();
    };

    const handleDetailsFileUpload = async (e, field, purpose) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading((u) => ({ ...u, [field]: true }));
        try {
            const orgId = selectedRole?.organization?._id || selectedRole?.organization;
            const res = await postDoctorImageUpload(file, {
                purpose,
                organizationId: orgId,
            });
            if (res.status === 201 && res.data?.file?._id) {
                setDetailsFileField(field, res.data.file);
                if (res.data.url) {
                    setPreviewUrls((p) => ({ ...p, [field]: res.data.url }));
                }
                Toast.fire({
                    icon: 'success',
                    title: 'File ready — click Save to keep it on your profile',
                });
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Upload failed';
            Toast.fire({ icon: 'error', title: msg });
        } finally {
            setUploading((u) => ({ ...u, [field]: false }));
            e.target.value = '';
        }
    };

    const viewSignature = async (url) => {
        let imageUrl = await getImageAsFile(process.env.REACT_APP_FILE_BASE_URL + '/' + encodeURIComponent(url))
        setSignUrl(imageUrl)
    }
    async function getImageAsFile(imageUrl) {
        try {
            let token = JSON.parse(localStorage.getItem("user"))?.token;
            const response = await fetch(imageUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const buffer = await response.arrayBuffer();
            const type = response.headers.get('Content-Type');
            const blob = new Blob([buffer], { type });
            return URL.createObjectURL(blob)
        } catch (error) {
            console.error('Error:', error);
            return null;
        }
    }

    useEffect(() => {
        if (detailsFileSlotId(info?.details?.files, 'signature')) return;
        if (info?.details?.signature) {
            viewSignature(info?.details?.signature)
        }
    }, [info?.details?.files, info?.details?.signature])

    useEffect(() => {
        let active = true;
        const fields = ['nidFront', 'nidBack', 'signature'];

        (async () => {
            const nextUrls = {};
            for (const field of fields) {
                if (previewUrls[field]) continue;
                const slot = info?.details?.files?.[field];
                const url = await detailsFileDisplayUrl(slot);
                if (url) nextUrls[field] = url;
            }
            if (active && Object.keys(nextUrls).length > 0) {
                setPreviewUrls((prev) => ({ ...prev, ...nextUrls }));
            }
        })();

        return () => { active = false; };
    }, [info?.details?.files, previewUrls]);

    const fileRow = (label, field, purpose) => (
        <div className="profile-file-upload mb-2">
            <span className="profile-file-upload__label">{label}</span>
            <div className="profile-file-upload__row">
                {previewUrls[field] && (
                    <img
                        alt=""
                        src={previewUrls[field]}
                        className="profile-file-upload__preview"
                    />
                )}
                {!previewUrls[field] && hasDetailsFileSlot(info?.details?.files, field) && (
                    <span className="text-muted small">File saved — choose a new file to replace</span>
                )}
                <label className="profile-file-upload__btn mb-0">
                    {uploading[field] ? 'Uploading…' : 'Choose file'}
                    <input
                        type="file"
                        accept={ACCEPT_IMAGES}
                        className="d-none"
                        onChange={(e) => handleDetailsFileUpload(e, field, purpose)}
                        disabled={!!uploading[field]}
                    />
                </label>
            </div>
        </div>
    );

    return (
        <div className='basic-info profile-panel'>

            <form onSubmit={onSubmit}>
                <div className="profile-panel__toolbar">
                    <div>
                        <h2 className="profile-panel__title">Professional details</h2>
                        <p className="profile-panel__subtitle">Registration, education, and practice settings.</p>
                    </div>
                    <button type="submit" className="btn save-button">
                        Save changes
                        {isSubmitting && (
                            <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true" />
                        )}
                    </button>
                </div>

                <div className="profile-fields">
                    <div className="profile-field profile-field--row mb-2">
                        <label className="profile-field__label form-label" htmlFor="bmdc-reg">BMDC registration no.</label>
                        <div className="profile-field__control">
                            <input
                                id="bmdc-reg"
                                className="form-control input"
                                autoComplete="off"
                                type="text"
                                placeholder="Registration number"
                                value={info?.details?.bmdcRegNo || info?.details?.bmdc_reg_no || ""}
                                onChange={(e) => setInfoFun("bmdcRegNo", e.target.value, "details")}
                            />
                        </div>
                    </div>

                    <div className="profile-field mb-2">
                        <label className="profile-field__label form-label">Education</label>
                        <textarea
                            autoComplete="off"
                            placeholder="Education"
                            value={info?.details?.education || ""}
                            onChange={(e) => setInfoFun("education", e.target.value, "details")}
                            rows={2}
                        />
                    </div>

                    <div className="profile-field mb-2">
                        <label className="profile-field__label form-label">Degree</label>
                        <textarea
                            autoComplete="off"
                            placeholder="Degree"
                            value={info?.details?.degree || ""}
                            onChange={(e) => setInfoFun("degree", e.target.value, "details")}
                            rows={2}
                        />
                    </div>

                    <div className="profile-field mb-2">
                        <label className="profile-field__label form-label">Additional speciality</label>
                        <textarea
                            autoComplete="off"
                            placeholder="Additional speciality"
                            value={info?.details?.additionalSpeciality || ""}
                            onChange={(e) => setInfoFun("additionalSpeciality", e.target.value, "details")}
                            rows={2}
                        />
                    </div>

                    <div className="profile-field profile-field--row mb-2">
                        <label className="profile-field__label form-label" htmlFor="practicing-since">Practicing since</label>
                        <div className="profile-field__control">
                            <input
                                id="practicing-since"
                                className="form-control input"
                                autoComplete="off"
                                type="date"
                                value={info?.details?.practicingSince?.slice(0, 10) || ""}
                                onChange={(e) => setInfoFun("practicingSince", e.target.value, "details")}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row mb-2">
                        <label className="profile-field__label form-label" htmlFor="per-day">Per day limit</label>
                        <div className="profile-field__control">
                            <input
                                id="per-day"
                                className="form-control input"
                                autoComplete="off"
                                type="number"
                                placeholder="Per day limit"
                                value={info?.details?.perDayLimit || ""}
                                onChange={(e) => setInfoFun("perDayLimit", e.target.value, "details")}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row mb-2">
                        <label className="profile-field__label form-label" htmlFor="sms-bal">SMS balance</label>
                        <div className="profile-field__control">
                            <input
                                id="sms-bal"
                                className="form-control input"
                                autoComplete="off"
                                type="number"
                                disabled
                                placeholder="Balance"
                                value={info?.details?.smsBalance || ""}
                                onChange={(e) => setInfoFun("smsBalance", e.target.value, "details")}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row mb-2">
                        <label className="profile-field__label form-label" htmlFor="fee-field">Fee</label>
                        <div className="profile-field__control">
                            <input
                                id="fee-field"
                                className="form-control input"
                                autoComplete="off"
                                type="number"
                                placeholder="Fee"
                                value={info?.details?.fee || ""}
                                onChange={(e) => setInfoFun("fee", e.target.value, "details")}
                            />
                        </div>
                    </div>

                    <div className="profile-upload-section mb-2">
                        <div className="profile-upload-section__title">NID &amp; signature</div>
                        {fileRow('NID — front', 'nidFront', FILE_UPLOAD_PURPOSE.DOCTOR_NID)}
                        {fileRow('NID — back', 'nidBack', FILE_UPLOAD_PURPOSE.DOCTOR_NID)}
                        {fileRow('Signature', 'signature', FILE_UPLOAD_PURPOSE.DOCTOR_SIGNATURE)}
                        {!previewUrls.signature && !detailsFileSlotId(info?.details?.files, 'signature') && info?.details?.signature && signUrl && (
                            <div className="profile-upload-section__legacy mb-0">
                                <p className="profile-legacy-note">Legacy signature — upload a new file to migrate to secure storage.</p>
                                <img className="profile-file-upload__preview" src={signUrl} alt="" />
                            </div>
                        )}
                    </div>

                    <div className="pt-3 mt-2 border-top d-flex justify-content-end">
                        <button
                            type="button"
                            className="profile-signout"
                            onClick={handleLogout}
                        >
                            <FontAwesomeIcon icon={faSignOut} size="sm" />
                            Sign out
                        </button>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default EducationalInfo
